// File: src/pages/FoundationActivities.jsx
// Description: Foundation Activities page - same structure as CurrentActivities
// Bilingual (EN/MM), sort latest/earliest, detail modal with gallery

import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/pages/foundation-activities.css';

// ─── API Base URL ─────────────────────────────────────────────────────────────
const BASE_URL = 'https://api.mahavihara-international.org';

// ─── Bilingual UI Labels ──────────────────────────────────────────────────────
const LABELS = {
  en: {
    pageTitle: 'Foundation Activities',
    pageSubtitle: 'Discover the work of our foundation',
    latest: 'Latest First',
    earliest: 'Earliest First',
    readMore: 'Read More',
    noData: 'No foundation activities found.',
    loading: 'Loading activities...',
    error: 'Failed to load activities. Please try again.',
    retry: 'Retry',
    gallery: 'Gallery',
    postedOn: 'Posted on',
  },
  mm: {
    pageTitle: 'မူလဌာနလှုပ်ရှားမှုများ',
    pageSubtitle: 'ကျွန်ုပ်တို့၏ မူလဌာန၏ လုပ်ဆောင်ချက်များကို ကြည့်ရှုပါ',
    latest: 'နောက်ဆုံးအရင်',
    earliest: 'အစောဆုံးအရင်',
    readMore: 'ဆက်ဖတ်ရန်',
    noData: 'မူလဌာနလှုပ်ရှားမှုများ မတွေ့ပါ။',
    loading: 'လှုပ်ရှားမှုများ ဖွင့်နေသည်...',
    error: 'လှုပ်ရှားမှုများ ဖွင့်မရပါ။ ထပ်မံကြိုးစားပါ။',
    retry: 'ထပ်ကြိုးစားရန်',
    gallery: 'ဓာတ်ပုံများ',
    postedOn: 'တင်သည့်ရက်',
  },
};

// ─── Sort options ─────────────────────────────────────────────────────────────
const SORT = { LATEST: 'latest', EARLIEST: 'earliest' };

// ─── Helper: format date ──────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// ─── Helper: resolve relative path to full URL ────────────────────────────────
const resolveUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BASE_URL}/${path}`;
};

// ─── Sub-component: Foundation Activity Card ──────────────────────────────────
const FoundationCard = ({ item, lang, labels, onOpen }) => {
  const title = lang === 'mm' ? item.title_mm : item.title_en;
  const body  = lang === 'mm' ? item.body_mm  : item.body_en;
  const galleryCount = item.more_photos?.length || 0;

  return (
    <article
      className="foundation-card scroll-fade hover-lift"
      onClick={() => onOpen(item)}
    >
      {/* Header image */}
      {item.header_photo && (
        <div className="foundation-card__image">
          <img
            src={resolveUrl(item.header_photo)}
            alt={title}
            loading="lazy"
          />
          {/* Gallery count badge */}
          {galleryCount > 0 && (
            <span className="foundation-card__gallery-badge">
              📷 +{galleryCount}
            </span>
          )}
        </div>
      )}

      <div className="foundation-card__body">
        {/* Date */}
        <span className="foundation-card__date">
          📅 {formatDate(item.created_at)}
        </span>

        {/* Title */}
        <h3 className={`foundation-card__title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
          {title}
        </h3>

        {/* Body preview — strip HTML tags, limit 120 chars */}
        <p className={`foundation-card__preview ${lang === 'mm' ? 'font-myanmar' : ''}`}>
          {body?.replace(/<[^>]+>/g, '').slice(0, 120)}
          {body?.length > 120 ? '...' : ''}
        </p>

        <button className="btn-primary foundation-card__btn">
          {labels.readMore}
        </button>
      </div>
    </article>
  );
};

// ─── Sub-component: Foundation Activity Modal ─────────────────────────────────
const FoundationModal = ({ item, lang, labels, onClose }) => {
  if (!item) return null;

  const title  = lang === 'mm' ? item.title_mm : item.title_en;
  const body   = lang === 'mm' ? item.body_mm  : item.body_en;
  const photos = item.more_photos || [];

  // Close on backdrop click
  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="foundation-modal__backdrop animate-fade-in-down"
      onClick={handleBackdrop}
    >
      <div className="foundation-modal__box animate-zoom-in">

        {/* Close button */}
        <button
          className="foundation-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Header photo */}
        {item.header_photo && (
          <div className="foundation-modal__header-img">
            <img src={resolveUrl(item.header_photo)} alt={title} />
          </div>
        )}

        <div className="foundation-modal__content">
          {/* Date */}
          <span className="foundation-card__date">
            {labels.postedOn}: {formatDate(item.created_at)}
          </span>

          {/* Title */}
          <h2 className={`foundation-modal__title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
            {title}
          </h2>

          {/* Body — rendered as HTML from backend */}
          <div
            className={`foundation-modal__body ${lang === 'mm' ? 'font-myanmar' : ''}`}
            dangerouslySetInnerHTML={{ __html: body }}
          />

          {/* Gallery grid */}
          {photos.length > 0 && (
            <div className="foundation-modal__gallery-section">
              <h4 className="foundation-modal__section-title">
                📷 {labels.gallery}
              </h4>
              <div className="foundation-modal__gallery">
                {photos.map((p, i) => (
                  <a
                    key={p.id ?? i}
                    href={resolveUrl(p.photo_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={resolveUrl(p.photo_path)}
                      alt={`gallery-${i + 1}`}
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page Component ──────────────────────────────────────────────────────
const FoundationActivities = () => {
  // currentLanguage: 'en' | 'my'
  const { currentLanguage } = useLanguage();
  const lang   = currentLanguage === 'my' ? 'mm' : 'en';
  const labels = LABELS[lang];

  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [sort, setSort]         = useState(SORT.LATEST);
  const [selected, setSelected] = useState(null);

  // ── Fetch foundation activities ──
  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/foundation-activities`);
      if (!res.ok) throw new Error('Network error');
      const data = await res.json();
      // Response: { success, message, data: [...] }
      setItems(data.data || data);
    } catch {
      setError(labels.error);
    } finally {
      setLoading(false);
    }
  }, [labels.error]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // ── Scroll-fade intersection observer ──
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e =>
        e.target.classList.toggle('visible', e.isIntersecting)
      ),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.scroll-fade').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  // ── Sort by created_at ──
  const sorted = [...items].sort((a, b) => {
    const da = new Date(a.created_at);
    const db = new Date(b.created_at);
    return sort === SORT.LATEST ? db - da : da - db;
  });

  return (
    <div className="foundation-page">

      {/* ── Page Banner ── */}
      <div className="page-banner">
        <div className="container">
          <div className="banner-content">
            <h1 className={`banner-title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
              {labels.pageTitle}
            </h1>
            <p className={`banner-subtitle ${lang === 'mm' ? 'font-myanmar' : ''}`}>
              {labels.pageSubtitle}
            </p>
          </div>
        </div>
      </div>

      {/* ── Sort Controls ── */}
      <div className="foundation-controls container">
        <div className="foundation-sort">
          <button
            className={`foundation-sort-btn ${sort === SORT.LATEST ? 'active' : ''}`}
            onClick={() => setSort(SORT.LATEST)}
          >
            🕐 <span className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.latest}</span>
          </button>
          <button
            className={`foundation-sort-btn ${sort === SORT.EARLIEST ? 'active' : ''}`}
            onClick={() => setSort(SORT.EARLIEST)}
          >
            🕰 <span className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.earliest}</span>
          </button>
        </div>
      </div>

      {/* ── Grid Section ── */}
      <section className="foundation-grid-section section-padding">
        <div className="container">

          {/* Loading */}
          {loading && (
            <div className="foundation-state text-center">
              <div className="spinner-ring" />
              <p className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.loading}</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="foundation-state foundation-state--error text-center">
              <p>{error}</p>
              <button className="btn-primary" onClick={fetchItems}>
                {labels.retry}
              </button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && sorted.length === 0 && (
            <div className="foundation-state text-center">
              <p className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.noData}</p>
            </div>
          )}

          {/* Grid */}
          {!loading && !error && sorted.length > 0 && (
            <div className="foundation-grid">
              {sorted.map(item => (
                <FoundationCard
                  key={item.id}
                  item={item}
                  lang={lang}
                  labels={labels}
                  onOpen={setSelected}
                />
              ))}
            </div>
          )}

        </div>
      </section>

      {/* ── Detail Modal ── */}
      {selected && (
        <FoundationModal
          item={selected}
          lang={lang}
          labels={labels}
          onClose={() => setSelected(null)}
        />
      )}

    </div>
  );
};

export default FoundationActivities;